import * as R from '../../Router/routes';
import { BundleBadge } from 'components/Bundle/BundleBadge/BundleBadge';
import {
  Button,
  ExtensionSchedule,
  Heading,
  Paragraph,
  Placeholder,
  RadioCard,
} from '../../components';
import {
  ChangeEvent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { DEFAULT_DATE_FORMAT, numberToCurrency } from '../../utils/translate';
import { DiscountTag } from './DiscountTag';
import { getBackUrlMenuScreens } from '../../utils/getUrl';
import { getToday } from 'utils/shiftedDateTime';
import { Helmet } from 'react-helmet-async';
import { isDiscountActive } from '../../utils/discounts';
import { urlBundlePreloadHashMap } from '../../Router/Router';
import { useAnalytics } from 'use-analytics';
import {
  useClient,
  useClientSettings,
  useDiscounts,
  useLatestLoan,
  usePaymentSchedule,
} from '../../hooks/webapi';
import { useFormik } from 'formik';
import { useLocation, useNavigate } from '@reach/router';
import { useTranslation } from 'react-i18next';
import classes from 'classnames';
import Dashboard from '../Layouts/Dashboard/Dashboard';
import dayjs from 'dayjs';
import styles from './ExtensionsNotOverdue.module.scss';
import Tracked from '../../customAnalytics/components/Tracked';
import useBundle from 'hooks/useBundle';
import useError from 'hooks/useError';
import ValidationError from 'utils/ValidationError';
import type { LoanExtension } from '../../hooks/webapi/useLatestLoan';

const INITIAL_VALUES = {
  extension: '',
};

const BUNDLE_EXTENSIONS_FEES: Array<LoanExtension> = [
  {
    term: 3,
    unit: 'DAYS',
    fee: 0.0,
    interest: 0.0,
    feeWithoutInterest: 0.0,
    feeBeforeDiscount: 0.0,
  },
];

const INITIAL_VALUES_LOAN_EXTENSIONS_FEES: Array<LoanExtension> = [
  {
    term: 7,
    unit: 'DAYS',
    fee: 0.0,
    interest: 0.0,
    feeWithoutInterest: 0.0,
    feeBeforeDiscount: 0.0,
    expirationDate: '',
  },
  {
    term: 14,
    unit: 'DAYS',
    fee: 0.0,
    interest: 0.0,
    feeWithoutInterest: 0.0,
    feeBeforeDiscount: 0.0,
    expirationDate: '',
  },
  {
    term: 30,
    unit: 'DAYS',
    fee: 0.0,
    interest: 0.0,
    feeWithoutInterest: 0.0,
    feeBeforeDiscount: 0.0,
    expirationDate: '',
  },
];

// @TODO: refactor the whole screen
export default memo(function Extensions() {
  const { t } = useTranslation('extensions');
  const { client } = useClient();
  const { latestLoan, setSelectedLoanExtension, selectedLoanExtension } =
    useLatestLoan();
  const { discounts, fetchDiscounts, largestExtensionDiscount } =
    useDiscounts();
  const { paymentSchedule, fetchPaymentSchedule } = usePaymentSchedule();
  const [loading, setLoading] = useState(true);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const { clientSettings, fetchClientSettings } = useClientSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const { track } = useAnalytics();
  const { showError } = useError();
  const { applyBundleExtension, isBundleExtensionAvailable } = useBundle();

  const isMini = latestLoan?.productCode === 'MINI';

  const fullExtensionFees = useMemo(() => {
    let loanExtensionFees =
      latestLoan?.loanExtensionFees || INITIAL_VALUES_LOAN_EXTENSIONS_FEES;

    const bundleExtensionFee = isBundleExtensionAvailable
      ? BUNDLE_EXTENSIONS_FEES
      : [];

    if (
      isMini &&
      latestLoan?.loanExtensionFees &&
      latestLoan?.loanExtensionFees[0].term !== 30
    ) {
      loanExtensionFees = loanExtensionFees.reverse();
    }

    return [...bundleExtensionFee, ...loanExtensionFees];
  }, [isBundleExtensionAvailable, isMini, latestLoan?.loanExtensionFees]);

  // Apply PID on url in case user has arrived to here externally without the product number
  useEffect(() => {
    const pidNotDefined =
      !location.search.includes('PID') ||
      !!location.search.includes('PID=undefined');
    if (pidNotDefined && latestLoan?.productNumber) {
      let queryParams = location.search;
      // ADD PID to the url
      if (!!location.search.includes('PID=undefined')) {
        queryParams = queryParams.replace(
          'PID=undefined',
          `PID=${latestLoan?.productNumber}`,
        );
      } else {
        const symbol = location.search.includes('?') ? '&' : '?';
        queryParams += `${symbol}PID=${latestLoan?.productNumber}`;
      }
      navigate(`${location.pathname}${queryParams}`);
    }
  }, [location.pathname, location.search, latestLoan?.productNumber, navigate]);

  useEffect(() => {
    (async () => {
      try {
        urlBundlePreloadHashMap[(R.EXTENSION_PAYMENT, R.NEWS_CONSENT)]();

        await Promise.all([
          fetchPaymentSchedule(),
          fetchDiscounts(),
          fetchClientSettings(),
        ]);

        setLoading(false);

        if (!latestLoan) {
          navigate(R.REPEATED_LOAN, { replace: true });

          return;
        }

        if (
          latestLoan &&
          latestLoan.loanExtensionFees &&
          latestLoan.loanExtensionFees.length < 1
        ) {
          navigate(R.ACTIVE_LOAN, { replace: true });

          return;
        }
      } catch {}
    })();
  }, [fetchDiscounts, latestLoan, fetchPaymentSchedule, setSelectedLoanExtension, fetchClientSettings, navigate]);

  const handleSubmit = useCallback(
    async (form: typeof INITIAL_VALUES) => {
      const selectedLoanExtension = fullExtensionFees.find(
        (extension) => extension.term.toString() === form.extension,
      );

      const isBundleExtensionSelected =
        selectedLoanExtension?.term === BUNDLE_EXTENSIONS_FEES[0]?.term;
      if (isBundleExtensionSelected) {
        try {
          setLoadingSubmit(true);
          await applyBundleExtension();
          navigate(R.ACTIVE_LOAN);
          return;
        } catch (e) {
          showError(e as ValidationError);
        } finally {
          setLoadingSubmit(false);
        }
      }

      setSelectedLoanExtension(selectedLoanExtension);
      !clientSettings?.acceptNews
        ? navigate(R.NEWS_CONSENT, {
            state: { nextUrl: R.EXTENSION_PAYMENT, selectedLoanExtension },
          })
        : navigate(R.EXTENSION_PAYMENT);
    },
    [
      fullExtensionFees,
      setSelectedLoanExtension,
      clientSettings?.acceptNews,
      navigate,
    ],
  );

  const formik = useFormik({
    initialValues: selectedLoanExtension
      ? { extension: selectedLoanExtension.term.toString() }
      : INITIAL_VALUES,
    onSubmit: handleSubmit,
  });

  const getExtensionCurrentTerm = useCallback(
    (dueDate: string) => {
      return (latestLoan?.productNumber === '6' ||
        latestLoan?.productNumber === '8') &&
        getToday() > new Date(dueDate)
        ? getToday()
        : dueDate;
    },
    [latestLoan],
  );

  const handleAnalytics = useCallback(
    (termString: string) => {
      const eventType = 'change';
      const data = {
        location: location.pathname,
        value: termString,
        description: 'Extenstion term selected',
        stateKey: 'extensions.termSelectedTs',
        valueKey: 'extensions.termSelectedValue',
      };

      track(eventType, data);
    },
    [location.pathname, track],
  );

  const onChangeHandler = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      handleAnalytics(e.target.value);
      formik.handleChange(e);
    },
    [formik, handleAnalytics],
  );

  useEffect(() => {
    if (
      latestLoan?.productCode === 'CONS' &&
      latestLoan?.isExtensionPossible &&
      fullExtensionFees?.[0]
    ) {
      formik.setFieldValue('extension', fullExtensionFees[0].term.toString());
    }
  }, [fullExtensionFees, latestLoan?.productCode]);

  const isActiveDiscountTag =
    !isMini ||
    (isMini &&
      isDiscountActive({
        amount: latestLoan?.amount,
        availableDiscounts: discounts,
      }));

  const renderRadioCard = useCallback(() => {
    return fullExtensionFees.map(
      ({ term, unit, fee, feeBeforeDiscount }, index) => {
        const checked = formik.values.extension === term.toString();
        const isDiscounted = fee < feeBeforeDiscount;
        /**
         * 30 days extension always had a discount.
         * Recurring extensions = before 31 days from last extension.
         * Discount was kept as long as the extensions were recurrent.
         * Business: As of 10 Aug 23, this case should remain disabled.
         * Tricky to improve/change logic's current state; asks for a whole refactor.
         * Suggest having a constant flag like: ARE_RECURRING_DISCOUNTED_EXTENSIONS_ENABLED
         */

        return (
          <RadioCard
            key={`${term}-${index}`}
            className={styles.radio}
            value={term.toString()}
            name="extension"
            id={term.toString()}
            onChange={onChangeHandler}
            checked={checked}
            dataTest={`loan-extension-${term}`}
          >
            <div className={styles['radio-content']}>
              <div className={styles['radio-labels']}>
                <Heading
                  className={classes(
                    checked && styles.checked,
                    styles['radio-label-days'],
                  )}
                  dataTest="extensionNoOverdue-heading"
                >
                  <Placeholder
                    loading={loading}
                    dataTest="extensionNoOverdue-heading-term"
                  >
                    {unit === 'DAYS'
                      ? t(`common:days_amount`, { count: term || 0 })
                      : t(`common:months_amount`, { count: term || 0 })}
                  </Placeholder>
                </Heading>
                <Paragraph
                  className={classes(checked && styles.checked)}
                  type="small"
                >
                  {latestLoan && (
                    <Placeholder
                      loading={loading}
                      dataTest="extensionNoOverdue-heading-date"
                    >
                      {t('date')}{' '}
                      {dayjs(getExtensionCurrentTerm(latestLoan?.dueDate))
                        .add(term, unit === 'DAYS' ? 'day' : 'month')
                        .format('DD/MM/YYYY')}
                    </Placeholder>
                  )}
                </Paragraph>
              </div>
              <div className={styles.price}>
                <div className={styles['fee-wrapper']}>
                  {isDiscounted && term === 30 && (
                    <Heading
                      className={classes(
                        styles['fee-heading'],
                        styles['old-fee'],
                        checked && styles['old-fee-checked'],
                      )}
                      dataTest="extensionNoOverdue-fee-heading"
                    >
                      <Placeholder loading={loading}>
                        {numberToCurrency(feeBeforeDiscount)}
                      </Placeholder>
                    </Heading>
                  )}
                  <Heading
                    className={classes(
                      styles['fee-heading'],
                      isDiscounted && styles.discounted,
                      checked && styles.checked,
                    )}
                    dataTest="extensionNoOverdue-fee-2-heading"
                  >
                    <Placeholder loading={loading}>
                      {numberToCurrency(fee)}
                    </Placeholder>
                  </Heading>
                </div>
              </div>
            </div>
          </RadioCard>
        );
      },
    );
  }, [
    formik.values.extension,
    isMini,
    latestLoan,
    fullExtensionFees,
    loading,
    onChangeHandler,
  ]);

  const extensionPaymentSchedule = useMemo(() => {
    if (latestLoan?.productCode !== 'CONS' || !latestLoan?.isExtensionPossible)
      return undefined;

    return paymentSchedule?.items
      .filter(({ status }) => status === undefined || status === 'DELAYED')
      .map(({ dueDate }) => ({
        currentDate: dayjs(getExtensionCurrentTerm(dueDate)).format(
          DEFAULT_DATE_FORMAT,
        ),
        newDate: dayjs(getExtensionCurrentTerm(dueDate))
          .add(
            fullExtensionFees[0]?.term,
            fullExtensionFees[0]?.unit === 'DAYS' ? 'day' : 'month',
          )
          .format(DEFAULT_DATE_FORMAT),
      }));
  }, [
    latestLoan?.isExtensionPossible,
    fullExtensionFees,
    latestLoan?.productCode,
    paymentSchedule?.items,
  ]);

  // useEffect to preselect first option by default (bundle changes)
  useEffect(() => {
    if (!selectedLoanExtension && isBundleExtensionAvailable)
      formik.setValues({
        extension: BUNDLE_EXTENSIONS_FEES[0].term.toString(),
      });
  }, [fullExtensionFees, isBundleExtensionAvailable, selectedLoanExtension]);

  return (
    <Dashboard
      small
      backUrl={getBackUrlMenuScreens(client, latestLoan)}
      className={styles['body-wrapper']}
    >
      <Helmet>
        <title>{t('html_title')}</title>
      </Helmet>

      <Heading
        className={styles.heading}
        size="l"
        dataTest="extensionNoOverdue-main-heading"
      >
        {t('heading')}
      </Heading>
      <Paragraph
        className={classes(
          styles.subtitle,
          isBundleExtensionAvailable && styles.bundle,
        )}
        dataTest="extensionNoOverdue-main-subtitle"
      >
        {t('subtitle')}
      </Paragraph>

      {!!largestExtensionDiscount && isActiveDiscountTag && (
        <DiscountTag
          discountPercent={largestExtensionDiscount.discountPercent}
          maxTerm={largestExtensionDiscount?.termRanges[0]?.max}
          testIdPrefix="extensionNoOverdue-main"
        />
      )}
      {isBundleExtensionAvailable && (
        <BundleBadge text={t('bundle:bundle_extension.badge')} />
      )}
      {latestLoan?.isExtensionPossible && (
        <form onSubmit={formik.handleSubmit}>
          <div className={styles.extensions}>
            {latestLoan?.productCode === 'CONS' ? (
              <ExtensionSchedule
                term={fullExtensionFees[0].term}
                unit={fullExtensionFees[0].unit}
                fee={fullExtensionFees[0].fee}
                feeBeforeDiscount={
                  largestExtensionDiscount?.discountPercent
                    ? fullExtensionFees[0].fee /
                      ((100 - largestExtensionDiscount?.discountPercent) / 100)
                    : undefined
                }
                schedule={extensionPaymentSchedule}
              />
            ) : (
              renderRadioCard()
            )}
          </div>
          <Tracked description="Continue button clicked">
            <Button
              className={styles.continue}
              disabled={!formik.values.extension}
              pending={loadingSubmit}
              type="submit"
              dataTest="continue"
            >
              {t('common:continue')}
            </Button>
          </Tracked>
        </form>
      )}
    </Dashboard>
  );
});
